import { useRef, useState } from "react";
import { Upload, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { TextField, AutoTextarea } from "@/components/tools/fields";

const V_STATUS = ["draft", "published", "frozen", "archived"];
const R_STATUS = ["draft", "published", "coming-soon", "archived"];

const NewVersion = ({ r, reload }) => {
  const file = useRef(null);
  const [f, setF] = useState({ version: "", release_notes: "", status: "published" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    Object.entries(f).forEach(([k, v]) => form.append(k, v));
    if (file.current?.files?.[0]) form.append("file", file.current.files[0]);
    setBusy(true);
    try {
      await api.post(`/admin/resources/${r.id}/versions`, form);
      toast.success(`Version ${f.version} saved`);
      setF({ version: "", release_notes: "", status: "published" });
      if (file.current) file.current.value = "";
      reload();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-4 grid gap-3 rounded-2xl border-2 border-dashed border-line p-4 md:grid-cols-[120px_minmax(0,1fr)_140px]" data-testid={`version-form-${r.slug}`}>
      <TextField id={`v-${r.id}`} label="Version" placeholder="v1.1" value={f.version} onChange={(v) => setF({ ...f, version: v })} testid={`version-number-${r.slug}`} />
      <AutoTextarea id={`vn-${r.id}`} label="Release notes / change log" rows={1} value={f.release_notes} onChange={(v) => setF({ ...f, release_notes: v })} testid={`version-notes-${r.slug}`} />
      <label className="flex flex-col gap-1.5 text-sm font-bold">Status
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="field-input" data-testid={`version-status-${r.slug}`}>{V_STATUS.map((s) => <option key={s}>{s}</option>)}</select>
      </label>
      <div className="flex flex-wrap items-end gap-3 md:col-span-3">
        <label className="flex flex-col gap-1.5 text-sm font-bold">PDF (optional, up to 40 MB)<input ref={file} type="file" accept=".pdf,application/pdf" className="field-input" data-testid={`version-file-${r.slug}`} /></label>
        <button type="submit" disabled={busy} className="btn btn-brand btn-sm" data-testid={`version-submit-${r.slug}`}><Upload size={14} aria-hidden /> {busy ? "Uploading…" : "Create version"}</button>
      </div>
    </form>
  );
};

const Details = ({ r, reload }) => {
  const [f, setF] = useState({ name: r.name, kind: r.kind, description: r.description, url: r.url, status: r.status, sort: r.sort });
  const save = async () => {
    try {
      await api.patch(`/admin/resources/${r.id}`, f);
      toast.success("Resource saved");
      reload();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_160px_auto] md:items-end">
      <TextField id={`rn-${r.id}`} label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid={`resource-name-${r.slug}`} />
      <TextField id={`ru-${r.id}`} label="Link (https://… or /page)" value={f.url} onChange={(v) => setF({ ...f, url: v })} testid={`resource-url-${r.slug}`} />
      <label className="flex flex-col gap-1.5 text-sm font-bold">Status
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="field-input" data-testid={`resource-status-${r.slug}`}>{R_STATUS.map((s) => <option key={s}>{s}</option>)}</select>
      </label>
      <button type="button" onClick={save} className="btn btn-ghost btn-sm" data-testid={`resource-save-${r.slug}`}><Save size={14} aria-hidden /> Save</button>
    </div>
  );
};

const Versions = ({ r, reload }) => {
  const setStatus = async (v, status) => {
    try {
      await api.patch(`/admin/versions/${v.id}`, { status });
      reload();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  if (!r.versions.length) return null;
  return (
    <table className="mt-4 w-full border-collapse text-sm" data-testid={`versions-${r.slug}`}>
      <thead><tr>{["Version", "Published", "File", "Notes", "Status"].map((h) => <th key={h} scope="col" className="border-b-2 border-line py-1.5 text-left">{h}</th>)}</tr></thead>
      <tbody>{r.versions.map((v) => (
        <tr key={v.id} className="border-b border-line align-top">
          <td className="py-2 font-bold">{v.version}</td><td>{v.publication_date}</td><td>{v.has_file ? `${v.filename} (${Math.round(v.size / 1024)} KB)` : "Online version"}</td><td className="max-w-[260px]">{v.release_notes}</td>
          <td><select aria-label={`Status of ${v.version}`} value={v.status} onChange={(e) => setStatus(v, e.target.value)} className="field-input py-1 text-sm" data-testid={`version-row-status-${v.version}`}>{V_STATUS.map((s) => <option key={s}>{s}</option>)}</select></td>
        </tr>
      ))}</tbody>
    </table>
  );
};

export const ResourcesAdmin = ({ items, reload }) => (
  <div className="space-y-4" data-testid="admin-resources">
    <p className="text-sm text-ink-soft">Publishing or freezing a version archives the previous published one. Downloads always serve the latest published or frozen version with a PDF; without a PDF, visitors get the online fill-in version.</p>
    {items.map((r) => (
      <section key={r.id} className="rounded-2xl border-2 border-line bg-surface p-5" data-testid={`admin-resource-${r.slug}`}>
        <p className="eyebrow mb-3">{r.kind}</p>
        <Details r={r} reload={reload} />
        {r.kind !== "external" && <><Versions r={r} reload={reload} /><NewVersion r={r} reload={reload} /></>}
      </section>
    ))}
  </div>
);
